/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { Header, Text, BrandColors } from '@blend/enterprise-components';
import { generateSanitizedHtml } from '@blend/shared-react';
import { UserBadge } from '../../../../../components/common/UserBadge/UserBadge';
import { getInitials, truncateMessage } from '../../../../../../src/utils/formatting';

const styles = StyleSheet.create({
  displayInline: { display: 'inline' },
  notesDetails: {
    cursor: 'pointer',
    display: 'flex',
    padding: '16px 0',
    ':focus': {
      outline: 0,
    },
  },
  notesDetailsLeftChild: { marginRight: 24 },
  notesDetailsRightChild: {
    marginLeft: 'auto',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  notesListItem: {
    backgroundColor: BrandColors.WHITE,
    display: 'flex',
    padding: '0 24px',
    border: '2px solid transparent',
    ':focus-within': {
      borderWidth: 2,
      borderColor: BrandColors.PRIMARY_400,
      borderStyle: 'solid',
    },
  },
  expandedContent: {
    borderTop: `1px solid ${BrandColors.NEUTRAL_150}`,
    paddingBottom: 32,
    paddingTop: 20,
    // This styles the raw html note content. Update this style if the nesting of Text within the expandedContent div
    // changes. span is the Text component.
    ' > span *': {
      // these are all the properties specified by the parent Text component. extend this as needed.
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      letterSpacing: 'inherit',
      lineHeight: 'inherit',
      '-webkit-font-smoothing': 'inherit',
    },
  },
  leftContainer: { flexGrow: 0, marginRight: 16, paddingTop: 16 },
  rightContainer: { flexGrow: 1 },
  textMarginBottom: { marginBottom: 4 },
  textMarginBottomRightChild: { marginBottom: 2 }, // line-height is 18 for Text and 16 for Header so needs less padding to keep them on same baseline
});
export namespace NotesListItem {
  export interface Props {
    noteId: string;
    notaryFullName: string;
    message: string;
    createdAt: Date;
    isOpen: boolean;
    toggleIsOpen: (noteId: string) => void;
  }
}

export const NotesListItem: React.FC<NotesListItem.Props> = ({ ...props }) => {
  const { isOpen, notaryFullName, message, createdAt, noteId } = props;

  const handleClick = (_event: React.SyntheticEvent) => {
    const { noteId, toggleIsOpen } = props;
    toggleIsOpen(noteId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { noteId, toggleIsOpen } = props;
    if (e.key === 'Enter') {
      toggleIsOpen(noteId);
    }
  };

  return (
    <div className={css(styles.notesListItem)} id="it-notesListItem">
      <div className={css(styles.leftContainer)}>
        <UserBadge data-it="NotesListItem-badge" label={getInitials(notaryFullName).toUpperCase()} />
      </div>
      <div className={css(styles.rightContainer)}>
        <div
          aria-expanded={isOpen}
          aria-controls={`NotesListItem-expandedContent-${noteId}`}
          className={css(styles.notesDetails)}
          id="it-NotesListItem-noteDetails"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
        >
          <div className={css(styles.notesDetailsLeftChild)}>
            <div className={css(styles.textMarginBottom)}>
              <Header fontWeight="SEMIBOLD" size={5}>
                {truncateMessage(message)}
              </Header>
            </div>
            <div>
              <div className={css(!isOpen && styles.displayInline, isOpen && styles.textMarginBottom)}>
                <Text fontSize="sm" colorName="NEUTRAL_700">
                  {notaryFullName}
                </Text>
              </div>
            </div>
          </div>
          <div className={css(styles.notesDetailsRightChild)}>
            <div className={css(styles.textMarginBottomRightChild)}>
              <Text fontSize="sm" colorName="NEUTRAL_900">
                {new Date(createdAt).toLocaleDateString()}
              </Text>
            </div>
            <div className={css(styles.textMarginBottomRightChild)}>
              <Text fontSize="sm" colorName="NEUTRAL_900">
                {new Date(createdAt).toLocaleTimeString([], { timeStyle: 'short' })}
              </Text>
            </div>
          </div>
        </div>
        {isOpen && (
          <div
            className={css(styles.expandedContent)}
            data-it="NotesListItem-expandedContent"
            id={`NotesListItem-expandedContent-${noteId}`}
          >
            {/* Update the expandedContent rule  ' > span *' if the nesting here changes */}
            <Text dangerouslySetInnerHTML={{ __html: generateSanitizedHtml(message) }} />
          </div>
        )}
      </div>
    </div>
  );
};
